import type { ReactNode } from "react";
import { TariiWord } from "../TariiWord";
import "./pitch.css";
import { PitchIcon, type PitchIconName } from "./PitchIcons";
import { PitchTile } from "./PitchTile";

export interface PlatformActor {
  icon: PitchIconName[];
  title: string;
  note: string;
}

export const PLATFORM_COPY = {
  actors: [
    { icon: ["planner"], title: "Planner", note: "On a laptop" },
    { icon: ["member", "chat"], title: "Team members", note: "Phone app or Telegram" },
    { icon: ["coordinator"], title: "Coordinator", note: "On the day" },
    { icon: ["management"], title: "Management", note: "Read-only view" },
  ] as PlatformActor[],
  inLabel: "Requests in",
  inNote: "Requests, sick reports, swaps",
  outLabel: "Roster and notifications out",
  outNote: "The roster, notifications, changes",
  platformName: (<TariiWord />) as ReactNode,
  checks: [
    "Collects requests",
    "Builds the roster",
    "Sends notifications",
    "Handles swaps and cover",
  ],
};

export interface PlatformDiagramProps {
  copy?: typeof PLATFORM_COPY;
  /** Optional brand mark, for example the logo image. */
  mark?: React.ReactNode;
}

/** `who.html`: the platform in the middle, four actors around it. */
export function PlatformDiagram({ copy = PLATFORM_COPY, mark }: PlatformDiagramProps) {
  return (
    <div className="pitch pitch-platform">
      <div className="pitch-grid pitch-grid-4">
        {copy.actors.map((a) => (
          <PitchTile
            key={a.title}
            title={a.title}
            icon={
              <span className="pitch-icon-row">
                {a.icon.map((n) => (
                  <PitchIcon key={n} name={n} size={a.icon.length > 1 ? 44 : 56} />
                ))}
              </span>
            }
          >
            {a.note}
          </PitchTile>
        ))}
      </div>
      <div className="pitch-flow">
        <div className="pitch-flow-item">
          <span className="pitch-arrow" data-dir="down" />
          <div>
            <p className="pitch-eyebrow">{copy.inLabel}</p>
            <p className="pitch-text">{copy.inNote}</p>
          </div>
        </div>
        <div className="pitch-flow-item">
          <span className="pitch-arrow" data-dir="up" />
          <div>
            <p className="pitch-eyebrow">{copy.outLabel}</p>
            <p className="pitch-text">{copy.outNote}</p>
          </div>
        </div>
      </div>
      <div className="pitch-platform-core">
        <div className="pitch-tile-head">
          {mark}
          <p className="pitch-big">{copy.platformName}</p>
        </div>
        <ul className="pitch-checks">
          {copy.checks.map((c) => (
            <li key={c}>
              <PitchIcon name="check" size={24} className="pitch-check" />
              {c}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
